import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Button,
  Form,
  Row,
  Col,
  Alert,
  Spinner,
  Container
} from 'react-bootstrap';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import productServiceAPI from '../services/productServiceAPI';
import factoryServiceAPI from '../services/factoryServiceAPI';
import imageService from '../services/imageService';
import CustomImage from '../components/CustomImage';
import AudioRecorder from '../components/AudioRecorder';
import { useLanguage } from '../contexts/LanguageContext';

const ProductCreate = () => {
  const { factoryId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [currentAudioUrl, setCurrentAudioUrl] = useState('');
  const [imageUrl, setImageUrl] = useState(location.state?.editingProduct?.imageUrl || '');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [dimL, setDimL] = useState(location.state?.editingProduct?.l || '');
  const [dimW, setDimW] = useState(location.state?.editingProduct?.w || '');
  const [dimH, setDimH] = useState(location.state?.editingProduct?.h || '');
  const [cbmValue, setCbmValue] = useState(location.state?.editingProduct?.cbm || '');
  const [autoCbm, setAutoCbm] = useState(false);

  const initDimensions = location.state?.editingProduct?.productDimensions;
  const [productDimensions, setProductDimensions] = useState(
    Array.isArray(initDimensions) && initDimensions.length > 0
      ? initDimensions
      : [{ prodL: '', prodW: '', prodH: '' }]
  );

  const [galleryImages, setGalleryImages] = useState(
    location.state?.editingProduct?.galleryImages || []
  );
  const [uploadingGalleryIdx, setUploadingGalleryIdx] = useState(null);

  const imageInputRef = useRef(null);
  const cameraTriggered = useRef(false);

  const editingProduct = location.state?.editingProduct || null;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const factoriesData = await factoryServiceAPI.getAllFactories();
      setFactories(factoriesData);
      setError(null);
    } catch (err) {
      setError(t('Erro ao carregar dados', '加载数据时出错'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!factoryId || editingProduct || loading || cameraTriggered.current) return;
    if (!imageInputRef.current) return;
    cameraTriggered.current = true;
    const timer = setTimeout(() => {
      imageInputRef.current?.click();
    }, 500);
    return () => clearTimeout(timer);
  }, [factoryId, editingProduct, loading]);

  useEffect(() => {
    if (autoCbm && dimL && dimW && dimH) {
      const calc = (parseFloat(dimL) * parseFloat(dimW) * parseFloat(dimH) / 1000000).toFixed(3);
      setCbmValue(calc);
    }
  }, [autoCbm, dimL, dimW, dimH]);

  const handleImageUpload = async (file) => {
    try {
      setUploadingImage(true);
      const uploadedImageUrl = await imageService.uploadFile(file);
      setImageUrl(uploadedImageUrl);
      return uploadedImageUrl;
    } catch (error) {
      setError(t('Erro no upload da imagem', '图片上传时出错'));
      throw error;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleGalleryUpload = async (file, idx) => {
    try {
      setUploadingGalleryIdx(idx);
      const url = await imageService.uploadFile(file);
      setGalleryImages(prev => {
        const next = [...prev];
        next[idx] = url;
        return next;
      });
    } catch (error) {
      setError(t('Erro no upload da imagem da galeria', '图库图片上传时出错'));
    } finally {
      setUploadingGalleryIdx(null);
    }
  };

  const addGallerySlot = () => {
    setGalleryImages(prev => [...prev, '']);
  };

  const removeGalleryImage = (idx) => {
    setGalleryImages(prev => prev.filter((_, i) => i !== idx));
  };

  const handlePreview = (imageSrc) => {
    setPreviewImage(imageSrc);
    setPreviewVisible(true);
  };

  const addProductDimension = () => {
    setProductDimensions(prev => [...prev, { prodL: '', prodW: '', prodH: '' }]);
  };

  const removeProductDimension = (idx) => {
    setProductDimensions(prev => prev.filter((_, i) => i !== idx));
  };

  const updateProductDimension = (idx, field, value) => {
    setProductDimensions(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const values = Object.fromEntries(formData.entries());

    try {
      setSubmitting(true);

      const finalValues = {
        ...values,
        cbm: cbmValue !== '' ? cbmValue : (values.cbm || null),
        imageUrl: imageUrl || values.imageUrl,
        audioUrl: currentAudioUrl || values.audioUrl,
        productDimensions: productDimensions.filter(d => d.prodL || d.prodW || d.prodH),
        galleryImages: galleryImages.filter(Boolean),
      };

      if (editingProduct) {
        await productServiceAPI.updateProduct(editingProduct.id, finalValues);
      } else {
        await productServiceAPI.createProduct(finalValues);
      }

      setImageUrl('');

      if (factoryId) {
        navigate(`/factory/${factoryId}`);
      } else {
        navigate('/products');
      }

    } catch (err) {
      setError(t('Erro ao salvar produto', '保存产品时出错'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (factoryId) {
      navigate(`/factory/${factoryId}`);
    } else {
      navigate('/products');
    }
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" />
          <p className="mt-3">{t('Carregando...', '加载中...')}</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">
              {editingProduct ? t('Editar Produto', '编辑产品') : t('Cadastrar Produto', '注册产品')}
            </h4>
            <Button variant="outline-secondary" onClick={handleCancel}>
              <i className="bi bi-arrow-left me-1"></i>
              {t('Voltar', '返回')}
            </Button>
          </div>
        </Card.Header>

        <Card.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            {/* Campo Fábrica e Botão Salvar */}
            <Row className="mb-3">
              {(factoryId || editingProduct?.factoryId) ? (
                <>
                  <Form.Control
                    type="hidden"
                    name="factoryId"
                    value={editingProduct?.factoryId || factoryId}
                  />
                  <Col xs={12}>
                    <Button
                      variant="success"
                      type="submit"
                      disabled={submitting || uploadingImage}
                      className="w-100"
                    >
                      {submitting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          {t('Salvando...', '保存中...')}
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-lg me-1"></i>
                          {editingProduct ? t('Atualizar', '更新') : t('Criar', '创建')}
                        </>
                      )}
                    </Button>
                  </Col>
                </>
              ) : (
                <>
                  <Col xs={8}>
                    <Form.Select name="factoryId" defaultValue="" required>
                      <option value="">{t('Selecione uma fábrica', '选择工厂')}</option>
                      {factories.map(factory => (
                        <option key={factory.id} value={factory.id}>
                          {factory.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={4}>
                    <Button
                      variant="success"
                      type="submit"
                      disabled={submitting || uploadingImage}
                      className="w-100"
                    >
                      {submitting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          {t('Salvando...', '保存中...')}
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-lg me-1"></i>
                          {editingProduct ? t('Atualizar', '更新') : t('Criar', '创建')}
                        </>
                      )}
                    </Button>
                  </Col>
                </>
              )}
            </Row>

            {/* Foto do produto */}
            <Form.Group className="mb-3">
              <Form.Label>{t('Foto do produto', '产品照片')}</Form.Label>
              <Form.Control
                ref={imageInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (file) {
                    try {
                      await handleImageUpload(file);
                    } catch (err) {
                    }
                  }
                }}
                disabled={uploadingImage}
              />
              {uploadingImage && (
                <div className="mt-2 text-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t('Enviando imagem...', '上传图片中...')}
                </div>
              )}
              {imageUrl && (
                <div className="mt-2">
                  <CustomImage
                    src={imageUrl}
                    alt="Preview"
                    className="img-fluid rounded"
                    style={{ maxHeight: '200px', maxWidth: '100%' }}
                    showPreview={true}
                    onPreview={handlePreview}
                  />
                </div>
              )}
              <Form.Control
                type="hidden"
                name="imageUrl"
                value={imageUrl}
              />
            </Form.Group>

            {/* Nome do Produto */}
            <Form.Group className="mb-3">
              <Form.Label>{t('Nome do Produto', '产品名称')}</Form.Label>
              <Form.Control
                type="text"
                name="name"
                defaultValue={editingProduct?.name || ''}
                placeholder={t('Digite o nome do produto', '输入产品名称')}
              />
            </Form.Group>

            {/* REF */}
            <Form.Group className="mb-3">
              <Form.Label>{t('REF', '参考号')}</Form.Label>
              <Form.Control
                type="text"
                name="ref"
                defaultValue={editingProduct?.ref || ''}
                placeholder={t('Digite a referência', '输入参考号')}
              />
            </Form.Group>

            {/* Medidas do Produto (múltiplas) */}
            <Form.Group className="mb-3">
              <Form.Label>{t('Medidas do Produto', '产品尺寸')}</Form.Label>
              {productDimensions.map((dim, idx) => (
                <Row key={idx} className="mb-2 align-items-center">
                  <Col xs={4}>
                    <Form.Control
                      type="number"
                      inputMode="decimal"
                      step="0.1"
                      placeholder={t('Prod - L (cm)', 'Prod - L (cm)')}
                      value={dim.prodL}
                      onChange={(e) => updateProductDimension(idx, 'prodL', e.target.value)}
                    />
                  </Col>
                  <Col xs={4}>
                    <Form.Control
                      type="number"
                      inputMode="decimal"
                      step="0.1"
                      placeholder={t('Prod - W (cm)', 'Prod - W (cm)')}
                      value={dim.prodW}
                      onChange={(e) => updateProductDimension(idx, 'prodW', e.target.value)}
                    />
                  </Col>
                  <Col xs={3}>
                    <Form.Control
                      type="number"
                      inputMode="decimal"
                      step="0.1"
                      placeholder={t('Prod - H (cm)', 'Prod - H (cm)')}
                      value={dim.prodH}
                      onChange={(e) => updateProductDimension(idx, 'prodH', e.target.value)}
                    />
                  </Col>
                  <Col xs={1} className="ps-0">
                    {productDimensions.length > 1 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeProductDimension(idx)}
                        className="px-1 py-0"
                        style={{ lineHeight: '1.8' }}
                      >
                        <i className="bi bi-x"></i>
                      </Button>
                    )}
                  </Col>
                </Row>
              ))}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={addProductDimension}
                className="mt-1"
              >
                <i className="bi bi-plus me-1"></i>
                {t('Adicionar medidas', '添加尺寸')}
              </Button>
            </Form.Group>

            {/* U.PRICE | UNIT */}
            <Row className="mb-3">
              <Col xs={6}>
                <Form.Group>
                  <Form.Label>{t('U.PRICE', '单价')}</Form.Label>
                  <Form.Control
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    name="uPrice"
                    defaultValue={editingProduct?.uPrice || ''}
                    placeholder={t('Digite o preço unitário', '输入单价')}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <Form.Label>{t('UNIT', '单位')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="unit"
                    defaultValue={editingProduct?.unit || 'PC'}
                    placeholder={t('Digite a unidade', '输入单位')}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* UNIT/CTN | CBM */}
            <Row className="mb-3">
              <Col xs={6}>
                <Form.Group>
                  <Form.Label>{t('UNIT/CTN', '单位/箱')}</Form.Label>
                  <Form.Control
                    type="number"
                    inputMode="numeric"
                    step="1"
                    name="unitCtn"
                    defaultValue={editingProduct?.unitCtn || ''}
                    placeholder={t('Digite unidades por caixa', '输入每箱单位')}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="mb-0">{t('CBM', 'CBM')}</Form.Label>
                    <Form.Check
                      type="checkbox"
                      id="auto-cbm-create"
                      label={t('Calcular', '自动')}
                      checked={autoCbm}
                      onChange={(e) => setAutoCbm(e.target.checked)}
                      style={{ fontSize: '0.8rem' }}
                    />
                  </div>
                  <Form.Control
                    type="number"
                    inputMode="decimal"
                    step="0.001"
                    name="cbm"
                    value={cbmValue}
                    onChange={(e) => setCbmValue(e.target.value)}
                    readOnly={autoCbm}
                    placeholder={t('Digite o CBM', '输入CBM')}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* L | W | H */}
            <Row className="mb-3">
              <Col xs={4}>
                <Form.Group>
                  <Form.Label>{t('L (cm)', 'L (cm)')}</Form.Label>
                  <Form.Control
                    type="number"
                    inputMode="decimal"
                    step="0.1"
                    name="l"
                    value={dimL}
                    onChange={(e) => setDimL(e.target.value)}
                    placeholder={t('Comprimento', '长度')}
                  />
                </Form.Group>
              </Col>
              <Col xs={4}>
                <Form.Group>
                  <Form.Label>{t('W (cm)', 'W (cm)')}</Form.Label>
                  <Form.Control
                    type="number"
                    inputMode="decimal"
                    step="0.1"
                    name="w"
                    value={dimW}
                    onChange={(e) => setDimW(e.target.value)}
                    placeholder={t('Largura', '宽度')}
                  />
                </Form.Group>
              </Col>
              <Col xs={4}>
                <Form.Group>
                  <Form.Label>{t('H (cm)', 'H (cm)')}</Form.Label>
                  <Form.Control
                    type="number"
                    inputMode="decimal"
                    step="0.1"
                    name="h"
                    value={dimH}
                    onChange={(e) => setDimH(e.target.value)}
                    placeholder={t('Altura', '高度')}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Peso unitário (g) */}
            <Form.Group className="mb-3">
              <Form.Label>{t('Peso unitário (g)', '单位重量(克)')}</Form.Label>
              <Form.Control
                type="number"
                inputMode="decimal"
                step="0.1"
                name="unitWeight"
                defaultValue={editingProduct?.unitWeight || ''}
                placeholder={t('Peso em gramas', '重量(克)')}
              />
            </Form.Group>

            {/* G.W */}
            <Form.Group className="mb-3">
              <Form.Label>{t('G.W', 'G.W')}</Form.Label>
              <Form.Control
                type="number"
                inputMode="decimal"
                step="0.1"
                name="gW"
                defaultValue={editingProduct?.gW || ''}
                placeholder={t('Digite o peso bruto', '输入毛重')}
              />
            </Form.Group>

            {/* REMARK */}
            <Form.Group className="mb-3">
              <Form.Label>{t('REMARK', '备注')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                name="remark"
                defaultValue={editingProduct?.remark || ''}
                placeholder={t('Digite observações adicionais', '输入额外备注')}
                className="remark-textarea"
              />
            </Form.Group>

            {/* Galeria de Fotos */}
            <Form.Group className="mb-3">
              <Form.Label>{t('Galeria de Fotos', '图片库')}</Form.Label>
              {galleryImages.map((imgUrl, idx) => (
                <div key={idx} className="mb-2 border rounded p-2">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <Form.Control
                      type="file"
                      accept="image/*"
                      disabled={uploadingGalleryIdx === idx}
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) await handleGalleryUpload(file, idx);
                      }}
                      style={{ flex: 1 }}
                    />
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeGalleryImage(idx)}
                      disabled={uploadingGalleryIdx === idx}
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  </div>
                  {uploadingGalleryIdx === idx && (
                    <div className="text-center mt-1">
                      <Spinner animation="border" size="sm" className="me-1" />
                      {t('Enviando...', '上传中...')}
                    </div>
                  )}
                  {imgUrl && (
                    <CustomImage
                      src={imgUrl}
                      alt={`Galeria ${idx + 1}`}
                      className="img-fluid rounded mt-1"
                      style={{ maxHeight: '150px', maxWidth: '100%' }}
                      showPreview={true}
                      onPreview={handlePreview}
                    />
                  )}
                </div>
              ))}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={addGallerySlot}
                className="mt-1"
              >
                <i className="bi bi-image me-1"></i>
                {t('Adicionar foto', '添加照片')}
              </Button>
            </Form.Group>

            {/* Gravação de Áudio */}
            <Form.Group className="mb-3">
              <AudioRecorder
                onAudioReady={(blob, url) => {
                }}
                onAudioChange={(url) => {
                  if (url) {
                    setCurrentAudioUrl(url);
                  }
                }}
                productId={editingProduct?.id || 'new'}
                initialAudioUrl={editingProduct?.audioUrls?.[0]?.url || editingProduct?.audioUrl}
                disabled={submitting}
              />
            </Form.Group>

            {/* Botões Cancelar e Salvar */}
            <div className="d-flex justify-content-between">
              <Button variant="secondary" onClick={handleCancel}>
                {t('Cancelar', '取消')}
              </Button>
              <Button
                variant="success"
                type="submit"
                disabled={submitting || uploadingImage}
              >
                {submitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {t('Salvando...', '保存中...')}
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-1"></i>
                    {editingProduct ? t('Atualizar', '更新') : t('Criar', '创建')}
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Modal de preview da imagem */}
      {previewVisible && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('Visualizar Imagem', '查看图片')}</h5>
                <Button
                  variant="outline-secondary"
                  onClick={() => setPreviewVisible(false)}
                >
                  <i className="bi bi-x"></i>
                </Button>
              </div>
              <div className="modal-body text-center">
                <img
                  src={previewImage}
                  alt="Preview"
                  className="img-fluid rounded"
                  style={{ maxHeight: '70vh' }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default ProductCreate;
